<template>
  <div>
    <!-- Bottom Sheet Form for Entering Account Info -->
    <BottomSheet ref="bottomSheet" hashBase="add_withdrawal_account">
      <div class="relative w-full p-6">
        <!-- Close Button -->
        <button
          @click="close"
          class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full transition-colors z-10"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div class="text-2xl font-bold title-color mb-1 pr-8">
          Nouveau compte de retrait
        </div>
        <p class="text-xs text-gray-500 mb-4">
          Ajoutez un compte Mobile Money pour recevoir vos gains
        </p>

        <!-- Informative Alert -->
        <div v-if="isFedaPayPilot" class="p-3.5 mb-5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 flex items-start gap-2.5">
          <span class="text-amber-600 text-base">ℹ️</span>
          <div class="text-xs leading-relaxed">
            <span class="font-bold">Vérification préalable :</span> Une demande de paiement de confirmation de <strong class="text-amber-700">1 FCFA</strong> vous sera envoyée sur ce numéro afin de vérifier que le compte vous appartient bien.
          </div>
        </div>

        <!-- Form Fields -->
        <div class="flex flex-col gap-4">
          <!-- Account Owner Name -->
          <div>
            <label class="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">
              Nom du titulaire / Propriétaire
            </label>
            <input
              v-model="ownerName"
              type="text"
              placeholder="Ex: Jean Dupont"
              class="w-full p-3.5 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all font-medium text-sm"
              required
            />
          </div>

          <!-- Phone Number -->
          <div>
            <label class="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">
              Numéro Mobile Money
            </label>
            <div class="relative">
              <input
                v-model="phoneNumber"
                type="tel"
                placeholder="Ex: 0151010580 ou 96000000"
                class="w-full p-3.5 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all font-mono font-medium text-sm"
                required
              />
            </div>
          </div>

          <!-- Operator -->
          <div>
            <label class="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">
              Opérateur réseau
            </label>
            <select
              v-model="operator"
              class="w-full p-3.5 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all font-medium text-sm"
            >
              <option value="MTN">MTN Mobile Money</option>
              <option value="MOOV">MOOV Money</option>
              <option value="CELTIIS">CELTIIS Cash</option>
              <option value="ORANGE">Orange Money</option>
              <option value="WAVE">Wave</option>
              <option value="TOGOCEL">T-Money (Togocel)</option>
            </select>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="mt-6">
          <button
            @click="proceedToVerification"
            :disabled="!isValidForm"
            class="w-full py-4 px-6 rounded-2xl font-black text-white shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:grayscale text-sm flex items-center justify-center gap-2"
            :class="isValidForm ? 'bg-primary shadow-primary/30 hover:shadow-xl' : 'bg-gray-400 shadow-none'"
          >
            <span>Continuer vers la vérification</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </div>
    </BottomSheet>

    <!-- Standalone FedaPay Pilot Verification Modal -->
    <AddFedapayWithdrawalAccount
      ref="fedapayVerification"
      @verified="onFedapayVerified"
      @cancelled="onFedapayCancelled"
      @error="onFedapayError"
    />

    <!-- Action Trigger to save account in MonRapidoo Accounts API -->
    <ActionTrigger
      ref="createAccountTrigger"
      dataKey="createWithdrawalAccount"
      @success="onAccountSaved"
      @failed="onAccountSaveFailed"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import BottomSheet from '@/presentation/components/dialog/BottomSheet.vue'
import AddFedapayWithdrawalAccount from './AddFedapayWithdrawalAccount.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import Storage from '@/shared/utils/storage'
import { getProviderToUse, checkFedapayAccount } from '@/project/data/services/withdrawal/withdrawalHelpers'

const bottomSheet = ref(null)
const fedapayVerification = ref(null)
const createAccountTrigger = ref(null)

const ownerName = ref('')
const phoneNumber = ref('')
const operator = ref('MTN')
const isFedaPayPilot = ref(false)

let openHandler = null

onMounted(() => {
  openHandler = Events.handle('open_add_withdrawal_account', () => {
    open()
  })
})

onUnmounted(() => {
  if (openHandler) {
    Events.removeHandler(openHandler)
  }
})

const isValidForm = computed(() => {
  return (
    ownerName.value.trim().length >= 2 &&
    phoneNumber.value.replace(/\s+/g, '').length >= 8 &&
    !!operator.value
  )
})

async function open() {
  const userData = Storage.get('userData') || {}
  const fullName = [userData.firstName, userData.lastName].filter(Boolean).join(' ') || userData.name || ''
  ownerName.value = fullName
  phoneNumber.value = userData.phoneNumber || ''
  operator.value = 'MTN'

  const provider = await getProviderToUse(false)
  isFedaPayPilot.value = (provider || '').toLowerCase() === 'fedapay-pilot'

  bottomSheet.value?.show()
}

function close() {
  bottomSheet.value?.hide()
}

async function proceedToVerification() {
  if (!isValidForm.value) return

  const cleanPhone = phoneNumber.value.replace(/\s+/g, '').trim()
  const rawPhone = cleanPhone.startsWith('229') && cleanPhone.length > 8 ? cleanPhone.slice(3) : cleanPhone

  const accountData = {
    owner: ownerName.value.trim(),
    accountLabel: ownerName.value.trim(),
    accountOwnerName: ownerName.value.trim(),
    rawPhoneNumber: rawPhone,
    phoneNumber: cleanPhone,
    operator: operator.value,
    country: 'Bénin',
    countryCode: 'BJ',
  }

  const provider = await getProviderToUse(false)
  const isFeda = (provider || '').toLowerCase() === 'fedapay-pilot'

  if (isFeda) {
    const checkRes = await checkFedapayAccount(rawPhone, operator.value)
    if (checkRes.exists && checkRes.isVerified) {
      close()
      onFedapayVerified(accountData)
      return
    }

    close()
    if (fedapayVerification.value) {
      fedapayVerification.value.startVerification(accountData)
    }
  } else {
    close()
    onFedapayVerified(accountData)
  }
}

function onFedapayVerified(verifiedData) {
  // Save into MonRapidoo Accounts API
  if (createAccountTrigger.value) {
    createAccountTrigger.value.trigger({
      accountLabel: verifiedData.owner || verifiedData.accountLabel,
      owner: verifiedData.owner,
      phoneNumber: verifiedData.phoneNumber || verifiedData.rawPhoneNumber,
      operator: verifiedData.operator,
      country: verifiedData.country || 'BJ',
    })
  }
}

function onFedapayCancelled() {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    type: 'info',
    title: 'Annulé',
    body: "L'ajout de compte a été interrompu.",
  })
}

function onFedapayError(err) {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    type: 'error',
    title: 'Erreur de vérification',
    body: err?.message || "Échec de la validation du compte.",
  })
}

function onAccountSaved(res) {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    type: 'success',
    title: 'Compte ajouté',
    body: 'Votre compte de retrait a été enregistré avec succès.',
  })
  Events.dispatch('withdrawal_account_created')
}

function onAccountSaveFailed(err) {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    type: 'error',
    title: 'Erreur d’enregistrement',
    body: err?.message || "Impossible d'enregistrer le compte de retrait.",
  })
}

defineExpose({
  open,
  close,
})
</script>

<script>
export default {
  name: 'AddNewWithdrawalAccount',
}
</script>
